import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useStableValue, useTableState } from "@/ds";
import type { FilterValue } from "@/ds";
import {
  DEFAULT_EMBARCACOES_SORT,
  EMBARCACAO_FILTER_COLUMNS,
  EMPTY_EMBARCACOES_FILTERS,
  hasEmbarcacoesFilters,
  isEmbarcacoesSortField,
  listEmbarcacoes,
} from "@/modules/embarcacoes";
import type {
  Embarcacao,
  EmbarcacoesFilters,
  EmbarcacoesSort,
  EmbarcacoesSortField,
} from "@/modules/embarcacoes";
import { EMBARCACOES_ERROR_MESSAGE, EMBARCACOES_FORBIDDEN_MESSAGE } from "../embarcacoes.constants";

interface ListaParams {
  busca: string;
  filters: EmbarcacoesFilters;
  page: number;
  pageSize: number;
  sort: EmbarcacoesSort;
}

/** Model da listagem: busca, filtros, página e ordenação no servidor; mudar um critério volta à página 1. */
export function useEmbarcacoesLista() {
  const [search, setSearch] = useState("");
  const [rawFilters, setFilters] = useState<EmbarcacoesFilters>(EMPTY_EMBARCACOES_FILTERS);
  const filters = useStableValue(rawFilters);
  const busca = search.trim();

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<EmbarcacoesSortField>({
    isSortField: isEmbarcacoesSortField,
    total: lastTotal,
    resetKey: JSON.stringify([busca, filters]),
  });
  // Sem ordenação escolhida, a consulta usa a padrão (Acrônimo, A-Z) e nenhum cabeçalho mostra a seta.
  const appliedSort: EmbarcacoesSort = tabela.sort ?? DEFAULT_EMBARCACOES_SORT;

  const lista = useAsyncList<ListaParams, Embarcacao>({
    fetcher: async (params, signal) => {
      const { embarcacoes, total } = await listEmbarcacoes({ ...params, signal });
      return { items: embarcacoes, total };
    },
    params: { busca, filters, page: tabela.page + 1, pageSize: tabela.rows, sort: appliedSort },
    debounceKey: busca,
    forbiddenMessage: EMBARCACOES_FORBIDDEN_MESSAGE,
    errorMessage: EMBARCACOES_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  return {
    search,
    filters,
    appliedSort,
    hasSearch: busca !== "",
    hasFilters: hasEmbarcacoesFilters(filters),
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    tabela,
    /** Sem espaço no começo (a busca não nasce de um espaço) e sem refazer o estado quando nada mudou. */
    buscar: (texto: string) => {
      const proximo = texto.trimStart();
      if (proximo !== search) setSearch(proximo);
    },
    clearSearch: () => setSearch(""),
    /** Os critérios do painel entram em conjunto; cada coluna aceita vários valores. */
    applyFilters: (value: FilterValue) =>
      setFilters(
        EMBARCACAO_FILTER_COLUMNS.reduce<EmbarcacoesFilters>(
          (next, column) => ({ ...next, [column]: value[column] ?? [] }),
          { ...EMPTY_EMBARCACOES_FILTERS },
        ),
      ),
    /** Devolve a consulta sem critério de filtragem; a busca permanece. */
    clearFilters: () => setFilters(EMPTY_EMBARCACOES_FILTERS),
  };
}

export type UseEmbarcacoesListaResult = ReturnType<typeof useEmbarcacoesLista>;
